import React from 'react'
import { EVENT_TYPE_CONSULTATION, EVENT_TYPE_THERAPY_SESSION } from 'src/consumer/constants'

// first level routes
const HomePage = React.lazy(() => import('./components/MentorHomePage'))
const PreferencesAndSettingsPage = React.lazy(() =>
  import('./components/PreferencesAndSettingsPage'),
)
const CalendarPage = React.lazy(() => import('./components/MentorCalendarPage'))
const MentorCommunicationPreference = React.lazy(() =>
  import('./components/MentorCommunicationPreference'),
)

const MentorClinicBookingsPage = React.lazy(() => import('./components/MentorClinicBookingsPage'))
const PatientListingPageForMentor = React.lazy(() =>
  import('./components/PatientListingPageForMentor'),
)
const LeftPatientListingWithRightContext = React.lazy(() =>
  import('./components/LeftPatientListingWithRightContext'),
)
const MentorPatientHistoryBasicPage = React.lazy(() =>
  import('./components/MentorPatientHistoryBasicPage'),
)
const MentorClinicSomeChamberAutoJoiningPage = React.lazy(() =>
  import('./components/MentorClinicSomeChamberAutoJoiningPage/index'),
)
// Mentor-scoped wrapper around the consumer LiveSessionScreen (see the `l/:bookedEventId` route
// below). It provides the consumer-layout SetupContext + InformationContext the session expects
// (they aren't app-level). Practitioners are redirected here from /l/* so the session runs under
// MentorLayout (mentor context available; detectCurrentPortal() resolves to 'mentor').
const MentorLiveSessionRoute = React.lazy(() => import('./components/MentorLiveSessionRoute'))
const MentorClinicAuditSomeChamber = React.lazy(() =>
  import('./components/MentorClinicAuditSomeChamber/index'),
)
const MentorClinicAuditChamberRoom = React.lazy(() =>
  import('./components/MentorClinicAuditChamberRoom/index'),
)
const MentorClinicAssociationsPage = React.lazy(() =>
  import('./components/MentorClinicBookingsPage/MentorClinicAssociationsPage'),
)
const PrescriptionTemplateViewPage = React.lazy(() =>
  import('./components/MentorPrescriptionTemplates/components/PrescriptionViewPage'),
)
const ClinicAssociationSelectionPageForPrescription = React.lazy(() =>
  import('./components/MentorPrescriptionTemplates'),
)
const MentorUpcomingAppointments = React.lazy(() =>
  import('./components/UpcomingAppointments/MentorUpcomingAppointments'),
)
// const ClinicAssociationSelectionPageForPrescription = React.lazy(() =>
//   import('./components/MentorPrescriptionTemplates'),
// )
const FeeCollectionReport = React.lazy(() => import('./components/FeeCollectionReport'))
const ProfilePage = React.lazy(() => import('./components/MentorProfilePage'))
const TherapistApplicationPage = React.lazy(() =>
  import('./components/MentorTherapistApplicationPage'),
)
// Phase 14e — self-service Stage 2 onboarding page applicants land on after support approves
// Stage 1. Lives separately from the regular profile page so the UX can stay focused.
const Stage2OnboardingPage = React.lazy(() => import('./components/Stage2OnboardingPage'))
const KycPendingActionsPage = React.lazy(() => import('./components/KycPendingActionsPage'))
const FAQsPage = React.lazy(() => import('./components/MentorFAQsPage'))
const MentorSettingsPage = React.lazy(() => import('./components/MentorSettingsPage'))
const LegalPageListing = React.lazy(() => import('./components/LegalTerms'))
const LegalPageForm = React.lazy(() => import('./components/LegalTerms/LegalForm'))
const PrescriptionMakingPage = React.lazy(() =>
  import('./components/PrescriptionMakingPage/PrescriptionMakingPageWithContext').catch((error) => {
    console.error('Error loading PrescriptionMakingPage:', error)
    return { default: () => React.createElement('div', null, 'Error loading PrescriptionMakingPage') }
  }),
)
const TherapyReportMakingPage = React.lazy(() => import('./components/TherapyReportMakingPage'))
const ScreeningReportMakingPage = React.lazy(() => import('./components/ScreeningReportMakingPage'))
const WebinarsListing = React.lazy(() => import('./components/WebinarsLisiting'))
const TeacherPayoutReports = React.lazy(() =>
  import('./components/TeacherPayoutReports/MentorPayoutReports.jsx'),
)

//////////////////////////// Online Course Components ////////////////////////////

const CourseCategories = React.lazy(() =>
  import('./components/OnlineCourse/OnlineCourseCategoryBoxes'),
)
const CoursesListing = React.lazy(() => import('./components/OnlineCourse/OnlineCourseListing'))
const CourseRoles = React.lazy(() => import('./components/OnlineCourse/OnlineCourseRoles'))

//////////////////////////// Activity Plan PageC Components ////////////////////////////
const ActivityPlanListing = React.lazy(() =>
  import('./components/ActivityPlanComponent/CustomPlans'),
)
const ActivityPlanForm = React.lazy(() =>
  import('./components/ActivityPlanComponent/CustomPlansForm'),
)

const ActiveFeeModels = React.lazy(() => import('./components/ActiveFeeModels/ActiveFeeModels'))
const EngagementModels = React.lazy(() => import('./components/ActiveFeeModels/EngagementModels'))

const OwnerPayoutReport = React.lazy(() => import('./components/PayoutReports/OwnerPayoutReport'))
const PayoutInvoices = React.lazy(() => import('./components/MentorInvoicesPage/MentorPayoutInvoices'))
const BusinessModelAndInvoicesPage = React.lazy(() =>
  import('./components/BusinessModelAndInvoices/BusinessModelAndInvoicesPage'),
)

export const primaryRoutes = [
  { path: 'kyc-pending-actions', exact: true, name: 'KYC Pending Actions', Element: KycPendingActionsPage },
  { path: 'profile/*', exact: true, name: 'Profile Page', Element: ProfilePage },
  {
    path: 'therapist-application',
    exact: true,
    name: 'Therapist Application',
    Element: TherapistApplicationPage,
  },
  {
    // Phase 14e — Stage 2 mentor-portal completion page. Two routes registered because react-
    // router 6.3 doesn't support `:practitionerId?` optional-segment syntax. With no id, the
    // server resolves the logged-in user's own record. The 14d email / WhatsApp templates link
    // to the id-pre-filled form so the URL works even before login (the auth gate redirects).
    path: 'onboarding/stage2',
    exact: true,
    name: 'Stage 2 Onboarding',
    Element: Stage2OnboardingPage,
  },
  {
    path: 'onboarding/stage2/:practitionerId',
    exact: true,
    name: 'Stage 2 Onboarding (id-scoped)',
    Element: Stage2OnboardingPage,
  },
  { path: 'webinars/', exact: true, name: 'Webinars Page', Element: WebinarsListing },
  { path: 'agreements/', exact: true, name: 'Legal Page', Element: LegalPageListing },
  { path: 'faqs/', exact: true, name: 'FAQs', Element: FAQsPage },
  {
    path: 'settings',
    exact: true,
    name: 'Settings',
    Element: MentorSettingsPage,
  },
  {
    path: 'agreements/:id',
    exact: true,
    name: 'Terms And Conditions Page',
    Element: LegalPageForm,
  },
  { path: 'my-calendar/*', exact: true, name: 'My Calendar Page', Element: CalendarPage },
  {
    path: 'my-clinic-associations/:clinicBrandId/:clinicId/emr-patients',
    exact: true,
    name: 'Patients Listing Page with patients on left and context on right',
    Element: LeftPatientListingWithRightContext,
  },
  {
    path: 'my-clinic-associations/:clinicBrandId/:clinicId/patient-list/:patientId',
    exact: true,
    name: 'Mentor Patients History',
    Element: MentorPatientHistoryBasicPage,
  },
  {
    path: 'my-clinic-associations/:clinicBrandId/:clinicId/patient-list',
    exact: true,
    name: 'Patients Listing Page',
    Element: PatientListingPageForMentor,
  },
  {
    path: 'my-clinic-associations/:clinicBrandId/:clinicId',
    exact: true,
    name: 'Mentor Clinic Bookings Page',
    Element: MentorClinicBookingsPage,
  },
  {
    path: 'join-chamber-room',
    exact: true,
    name: 'MentorClinicSomeChamberAutoJoiningPage',
    Element: MentorClinicSomeChamberAutoJoiningPage,
  },
  {
    path: 'join-chamber-room/:clinicBrandId/:clinicId',
    exact: true,
    name: 'MentorClinicSomeChamberAutoJoiningPage',
    Element: MentorClinicSomeChamberAutoJoiningPage,
  },
  {
    path: 'join-chamber-room/:clinicBrandId/:clinicId/l/:sessionSlug',
    exact: true,
    name: 'MentorClinicSomeChamberAutoJoiningPageWithSession',
    Element: MentorClinicSomeChamberAutoJoiningPage,
  },
  {
    // Mentor-scoped live session. Renders the SAME LiveSessionScreen as the consumer /l/ route,
    // but nested under MentorLayout so mentor context stays mounted and detectCurrentPortal()
    // resolves to 'mentor'. Practitioners are redirected here from /l/* (see LiveSessionScreen).
    // Full-bleed (no sidebar / no content padding) via 'l' in MentorLayout noSidebarPagesPaths.
    path: 'l/:bookedEventId',
    exact: true,
    name: 'Mentor Live Session',
    Element: MentorLiveSessionRoute,
    withSlug: true,
  },
  {
    path: 'audit-chamber-room',
    exact: true,
    name: 'MentorClinicAuditSomeChamber',
    Element: MentorClinicAuditSomeChamber,
  },
  {
    path: 'join-as-auditor/:chamberRoomId/:channelName',
    exact: true,
    name: 'MentorClinicAuditChamberRoom',
    Element: MentorClinicAuditChamberRoom,
  },
  {
    path: 'my-clinic-associations',
    exact: true,
    name: 'Mentor Clinic Associations Page',
    Element: MentorClinicAssociationsPage,
  },
  {
    path: 'preferences-and-settings',
    exact: true,
    name: 'Preferences and Settings Page',
    Element: PreferencesAndSettingsPage,
  },
  {
    path: 'stats',
    exact: true,
    name: 'Stats Page',
    Element: MentorClinicAssociationsPage,
  },
  {
    path: 'manage-my-bookings',
    exact: true,
    name: 'Manage My Bookings Page',
    Element: MentorClinicAssociationsPage,
  },
  {
    path: 'communication-preference',
    exact: true,
    name: 'Mentor Clinic Associations Page',
    Element: MentorCommunicationPreference,
  },
  {
    path: 'prescription-templates',
    exact: true,
    name: 'Clinic Selection For Prescription Templates',
    Element: ClinicAssociationSelectionPageForPrescription,
  },
  {
    path: 'prescription-templates/:clinicId',
    exact: true,
    name: 'Prescription Template in clinic',
    Element: PrescriptionTemplateViewPage,
  },
  {
    path: 'fee-collection-report',
    exact: true,
    name: 'Fee Collection Report',
    Element: FeeCollectionReport,
  },
  //////////////////////////// Online Courses Routes ////////////////////////////

  { path: 'courses', exact: true, name: 'Course Categories', Element: CourseCategories },
  { path: 'courses/:courseCategory', exact: true, name: 'Courses', Element: CourseRoles }, // TODO: need to change the Component that is being used
  {
    path: 'courses/:courseCategory/my-courses',
    exact: true,
    name: 'Courses',
    Element: CoursesListing,
  }, // TODO: need to change the import location

  //////////////////////////// Activity Plan Routes ////////////////////////////
  { path: 'homework-plans/', exact: true, name: 'Activity Plans', Element: ActivityPlanListing },
  {
    path: 'homework-plans/:id',
    exact: true,
    name: 'Activity Plans Form',
    Element: ActivityPlanForm,
  },
  ////////////// Not being actively used, remove intent /////////////////////////////
  {
    path: 'prescription/*',
    exact: true,
    name: 'Prescription Writing Page',
    Element: PrescriptionMakingPage,
  },
  {
    path: 'therapy-report/*',
    exact: true,
    name: 'Prescription Writing Page',
    Element: TherapyReportMakingPage,
  },
  {
    path: 'screening-report/*',
    exact: true,
    name: 'Screening Report Page',
    Element: ScreeningReportMakingPage,
  },
  {
    path: 'clinic-payout-reports/consultation',
    exact: true,
    name: 'Clinic Payout Reports consultation',
    Element: OwnerPayoutReport,
    eventType: EVENT_TYPE_CONSULTATION,
    showSummaryReport: true,
  },
  {
    path: 'clinic-payout-reports/therapy',
    exact: true,
    name: 'Clinic Payout Reports therapy',
    Element: OwnerPayoutReport,
    eventType: EVENT_TYPE_THERAPY_SESSION,
    showSummaryReport: true,
  },
  {
    path: 'payout-reports',
    exact: true,
    name: 'Payout Reports',
    Element: TeacherPayoutReports,
    eventType: EVENT_TYPE_THERAPY_SESSION,
    showSummaryReport: true,
  },
  {
    path: 'business-models',
    exact: true,
    name: 'Clinic Payout Reports therapy',
    Element: ActiveFeeModels,
    // eventType: EVENT_TYPE_THERAPY_SESSION,
    // showSummaryReport: true,
  },
  {
    path: 'engagement-models',
    exact: true,
    name: 'Engagement Models',
    Element: EngagementModels,
    // eventType: EVENT_TYPE_THERAPY_SESSION,
    // showSummaryReport: true,
  },
  {
    path: 'payout-invoices',
    exact: true,
    name: 'Payout Invoices',
    Element: PayoutInvoices,
    // eventType: EVENT_TYPE_THERAPY_SESSION,
    // showSummaryReport: true,
  },
  {
    path: 'business-and-invoices',
    exact: true,
    name: 'Business Model & Invoices',
    Element: BusinessModelAndInvoicesPage,
  },
  {
    path: 'upcoming-appointments',
    exact: true,
    name: 'Upcoming Appointments',
    Element: MentorUpcomingAppointments,
  },
  { path: '/', exact: true, name: 'Home', Element: HomePage },
]

// second level routes for my calendar

const AvailabilityPage = React.lazy(() => import('./components/MentorAvailabilityPage'))
const EventsPage = React.lazy(() => import('./components/MentorEventsPage'))
const CalendarIntegrationsPage = React.lazy(() =>
  import('./components/MentorCalendarIntegrationsPage'),
)

export const secondaryRoutes = [
  { path: '', exact: true, name: 'My Events', Element: EventsPage },
  { path: 'availability', exact: true, name: 'Availability', Element: AvailabilityPage },
  {
    path: 'integrations',
    exact: true,
    name: 'Calendar Integrations',
    Element: CalendarIntegrationsPage,
  },
]
